import { useEffect, useMemo, useState } from "react";
import { parseReceiptMarkup } from "@/lib/receiptMarkup";
import { renderReceiptLogoPreview } from "@/lib/receiptLogoPreview";
import {
  buildPickupReceiptPreviewModel,
  type PickupReceiptPreviewOptions,
} from "@/lib/pickupReceiptPreview";
import {
  receiptPreviewFontFamilyCss,
  receiptPreviewFontSizePx,
  type ReceiptPreviewLine,
} from "@/lib/receiptPreview";

interface PickupReceiptPreviewProps extends Omit<PickupReceiptPreviewOptions, "logoConfigured"> {
  logoData?: string;
  logoWidthPercent?: number;
}

function ReceiptFormattedLine({
  text,
  baseFontSizePx,
}: {
  text: string;
  baseFontSizePx: number;
}) {
  const segments = useMemo(() => parseReceiptMarkup(text), [text]);

  return (
    <span className="whitespace-pre">
      {segments.map((segment, index) => {
        if (!segment.text) {
          return null;
        }
        const className = [
          segment.style.bold ? "font-bold" : "",
          segment.style.italic ? "italic" : "",
          segment.style.underline ? "underline" : "",
        ]
          .filter(Boolean)
          .join(" ");
        const style = segment.style.extraLarge
          ? {
              display: "inline-block" as const,
              fontSize: `${Math.round(baseFontSizePx * 1.85)}px`,
              lineHeight: 1.15,
            }
          : undefined;

        return (
          <span key={`${index}-${segment.text}`} className={className || undefined} style={style}>
            {segment.text}
          </span>
        );
      })}
    </span>
  );
}

function PickupReceiptPreviewLineView({
  line,
  baseFontSizePx,
}: {
  line: ReceiptPreviewLine;
  baseFontSizePx: number;
}) {
  if (!line.text && line.kind !== "footer") {
    return <div className="h-2" />;
  }

  return (
    <div
      className={[
        "leading-relaxed",
        line.align === "center" ? "text-center" : "",
        line.align === "right" ? "text-right" : "",
        line.kind === "title" ? "font-semibold tracking-[0.2em]" : "",
        line.kind === "emphasis" ? "font-semibold" : "",
        line.kind === "separator" ? "text-label-tertiary" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ReceiptFormattedLine text={line.text} baseFontSizePx={baseFontSizePx} />
    </div>
  );
}

export function PickupReceiptPreview({
  headerText,
  footerText,
  logoData,
  charactersPerLine,
  separatorStyle,
  headerAlign,
  footerAlign,
  titleText,
  fontSize,
  fontFamily,
  logoWidthPercent,
  guestName,
  pitchCode,
  items,
}: PickupReceiptPreviewProps) {
  const [processedLogo, setProcessedLogo] = useState<string | null>(null);

  const model = useMemo(
    () =>
      buildPickupReceiptPreviewModel({
        headerText,
        footerText,
        logoConfigured: Boolean(logoData),
        charactersPerLine,
        separatorStyle,
        headerAlign,
        footerAlign,
        titleText,
        fontSize,
        fontFamily,
        guestName,
        pitchCode,
        items,
      }),
    [
      charactersPerLine,
      footerAlign,
      footerText,
      fontFamily,
      fontSize,
      guestName,
      headerAlign,
      headerText,
      items,
      logoData,
      pitchCode,
      separatorStyle,
      titleText,
    ],
  );

  useEffect(() => {
    if (!logoData) {
      setProcessedLogo(null);
      return;
    }

    let cancelled = false;
    void renderReceiptLogoPreview(logoData, charactersPerLine ?? 32, logoWidthPercent)
      .then((preview) => {
        if (!cancelled) {
          setProcessedLogo(preview);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProcessedLogo(logoData);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [charactersPerLine, logoData, logoWidthPercent]);

  const bodyStartIndex = model.lines.findIndex((line) => line.kind === "title");
  const baseFontSizePx = receiptPreviewFontSizePx(model.fontSize);

  return (
    <div className="rounded-xl border border-separator bg-grouped p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-label-secondary">Abholungsbon-Vorschau</span>
        <span className="text-xs text-label-tertiary">{model.width} Zeichen · Drucksimulation</span>
      </div>

      <div className="mt-3 flex justify-center">
        <div
          className="w-full max-w-[24rem] rounded-sm border border-black/10 bg-white px-4 py-5 text-black shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
          style={{
            fontFamily: receiptPreviewFontFamilyCss(model.fontFamily),
            fontSize: `${receiptPreviewFontSizePx(model.fontSize)}px`,
          }}
          aria-label="Abholungsbon-Vorschau"
        >
          {model.lines.slice(0, bodyStartIndex >= 0 ? bodyStartIndex : model.lines.length).map((line, index) => (
            <PickupReceiptPreviewLineView key={`pre-${index}-${line.text}`} line={line} baseFontSizePx={baseFontSizePx} />
          ))}

          {processedLogo ? (
            <div className="my-3 flex justify-center">
              <img
                src={processedLogo}
                alt="Abholungsbon-Logo Druckvorschau"
                className="h-auto max-w-full object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            </div>
          ) : null}

          {model.lines.slice(bodyStartIndex >= 0 ? bodyStartIndex : model.lines.length).map((line, index) => (
            <PickupReceiptPreviewLineView key={`post-${index}-${line.text}`} line={line} baseFontSizePx={baseFontSizePx} />
          ))}
        </div>
      </div>

      {logoData ? (
        <p className="mt-2 text-xs text-label-tertiary">
          Logo-Vorschau entspricht der ESC/POS-Aufbereitung (Schwarzweiß, skaliert auf gewählte Logo-Breite).
        </p>
      ) : null}
    </div>
  );
}
